import { ActionIcon, Breadcrumbs, Group, Text, Tooltip } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  FiArrowLeft,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiPlay,
  FiSettings,
} from 'react-icons/fi';
import { t } from 'i18next';
import { useStore } from '@/store';
import { useGraphContext } from '../../context/graph/hook';
import { BreadcrumbAction, StyledHeader } from './styles';
import { useGraphBreadcrumbTitles } from './use-graph-breadcrumb-titles';

interface GraphEditorHeaderProps {
  onRun?: () => void;
  onOpenConfig?: () => void;
}

export const GraphEditorHeader = ({
  onRun,
  onOpenConfig,
}: GraphEditorHeaderProps) => {
  /* Global state */
  const getName = useStore(({ entries: e }) => e.getName);
  /* Context state */
  const [history, undo, redo, ref] = useGraphContext(({ cmd, graph: g }) => [
    g.getHistory(),
    cmd.undo,
    cmd.redo,
    g.getRef(),
  ]);
  const nodeTitles = useGraphBreadcrumbTitles();
  /* Hooks */
  const navigate = useNavigate();
  /* Handlers */
  const handleFlowExit = () => {
    navigate(-1);
  };

  return (
    <StyledHeader>
      <Group spacing="xs">
        <ActionIcon
          variant="subtle"
          radius="md"
          size="sm"
          onClick={handleFlowExit}
          aria-label={t('graphEditor.header.goBack')}
        >
          <FiArrowLeft size={16} />
        </ActionIcon>
        <BreadcrumbAction
          variant="subtle"
          size="sm"
          aria-label={t('graphEditor.header.breadcrumbs')}
        >
          <Breadcrumbs separator="/">
            <Text size="xs" fw={500}>
              {getName(ref.fieldId)}
            </Text>
            {nodeTitles.map((title, index) => (
              <Text key={`${title}-${index}`} size="xs" fw={500}>
                {title}
              </Text>
            ))}
          </Breadcrumbs>
        </BreadcrumbAction>
      </Group>
      <Group spacing="xs">
        <Tooltip label={t('graphEditor.header.undo')} withArrow>
          <ActionIcon
            variant="transparent"
            radius="md"
            size="sm"
            onClick={undo}
            aria-label={t('graphEditor.header.undo')}
            disabled={history.past.length === 0}
          >
            <FiCornerUpLeft size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('graphEditor.header.redo')} withArrow>
          <ActionIcon
            variant="transparent"
            radius="md"
            size="sm"
            onClick={redo}
            aria-label={t('graphEditor.header.redo')}
            disabled={history.future.length === 0}
          >
            <FiCornerUpRight size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('graphEditor.header.runGraph')} withArrow>
          <ActionIcon
            variant="subtle"
            radius="md"
            size="sm"
            onClick={onRun}
            aria-label={t('graphEditor.header.runGraph')}
          >
            <FiPlay size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('graphEditor.header.configuration')} withArrow>
          <ActionIcon
            variant="subtle"
            radius="md"
            size="sm"
            onClick={onOpenConfig}
            aria-label={t('graphEditor.header.configuration')}
          >
            <FiSettings size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </StyledHeader>
  );
};
